<script setup lang="ts">
import { computed, useId } from 'vue'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
  BOOTSTRAP_MENU_CLASSES,
  BOOTSTRAP_MENU_OPTION_CLASSES,
  BOOTSTRAP_MENU_SIDE_OFFSET,
} from '@/components/dropdown-menu'
import { capitalize } from '@/util/strings'
import { getAnalyticsTracker } from '@/tracking/analytics/plugin'
import { EVENT_NAMES } from '@/tracking/analytics/event_names'

const props = defineProps<{
  languages: string[]
}>()

const selectedLanguage = defineModel<string>({ default: '' })

const analytics = getAnalyticsTracker()

const labelId = useId()
const valueId = useId()

const displayLanguage = computed(() => selectedLanguage.value || props.languages[0] || '')

const trackOpened = (open: boolean) => {
  if (open) {
    analytics.sendEvent(EVENT_NAMES.RESOURCES.CLICKED_CHANGE_LANGUAGE)
  }
}

const selectLanguage = (language: string) => {
  selectedLanguage.value = language
}
</script>

<template>
  <div class="leading-4">
    <span class="mx-1.5 text-md">
      <span class="material-icons-round align-text-bottom text-md text-[rgb(140,140,140)]" aria-hidden="true">public</span> <span :id="labelId">Assessment Language: </span>
    </span>
    <span v-if="languages.length === 1">{{ capitalize(languages[0]) }}</span>
    <!-- non-modal: a modal menu marks the rest of the page aria-hidden while it is open,
         which axe reports as aria-hidden-focus -->
    <DropdownMenu v-else :modal="false" @update:open="trackOpened">
      <DropdownMenuTrigger
        :aria-labelledby="`${labelId} ${valueId}`"
        class="inline-block whitespace-nowrap rounded-[3px] border border-[rgb(140,140,140)] bg-white px-2.5 py-[5px] text-center align-middle text-sm leading-normal text-[#636363] hover:border-[#6d6d6d] hover:bg-gray-300 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-400 data-[state=open]:bg-gray-300 data-[state=open]:shadow-[inset_0_3px_5px_rgba(0,0,0,0.125)]"
      >
        <span :id="valueId">{{ capitalize(displayLanguage) }}</span> <span
          class="inline-block h-0 w-0 border-x-4 border-b-0 border-t-4 border-x-[transparent] border-t-[currentColor] align-middle"
          aria-hidden="true"
        />
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        :side-offset="BOOTSTRAP_MENU_SIDE_OFFSET"
        :class="BOOTSTRAP_MENU_CLASSES"
      >
        <DropdownMenuRadioGroup :model-value="displayLanguage" @update:model-value="selectLanguage">
          <DropdownMenuRadioItem
            v-for="language in languages"
            :key="language"
            :value="language"
            :class="BOOTSTRAP_MENU_OPTION_CLASSES"
          >
            {{ capitalize(language) }}
          </DropdownMenuRadioItem>
        </DropdownMenuRadioGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  </div>
</template>
